import type { RefillMedLine } from '@seen/core/medication-refill/decideRefillActions'
import type { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { cn, ItemContent } from '@seen/ui'
import type { ReactNode } from 'react'
import { Loader } from '#src/components/core/Loader.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { MedicationElationDraftActions } from '#src/components/medications/MedicationElationDraftActions.tsx'

interface MedicationRefillBlockProps {
  meds: readonly RefillMedLine[]
  medicationById?: ReadonlyMap<string, ExtendedMedicationRequest>
  isLoading?: boolean
  hasParticipant?: boolean
  onDraftPrescription?: (
    medication: ExtendedMedicationRequest,
    orderType: 'Refill'
  ) => void | Promise<number | undefined>
  onOpenDraftPrescription?: (
    draftId: number | string,
    medication: ExtendedMedicationRequest
  ) => void
  draftIdsByThreadId?: ReadonlyMap<string, number | string>
  draftingMedicationIds?: ReadonlySet<string>
  className?: string
}

export function MedicationRefillBlock({
  meds,
  medicationById,
  isLoading,
  hasParticipant = true,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftIdsByThreadId,
  draftingMedicationIds,
  className,
}: MedicationRefillBlockProps) {
  return (
    <div className={cn('flex flex-col rounded-t-md border', className)}>
      <div className="flex h-[50px] items-center gap-2 rounded-t-md border-b bg-frame px-4">
        <div className="flex items-center gap-2 text-sm leading-snug font-medium">
          Medications Needing Refills
          {!isLoading && hasParticipant && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-border bg-background px-1 text-xs font-normal text-muted-foreground tabular-nums">
              {meds.length}
            </span>
          )}
        </div>
      </div>
      <ItemContent className="px-4 py-2">
        {refillBlockBody({
          meds,
          medicationById,
          isLoading,
          hasParticipant,
          onDraftPrescription,
          onOpenDraftPrescription,
          draftIdsByThreadId,
          draftingMedicationIds,
        })}
      </ItemContent>
    </div>
  )
}

function refillBlockBody({
  meds,
  medicationById,
  isLoading,
  hasParticipant,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftIdsByThreadId,
  draftingMedicationIds,
}: Omit<MedicationRefillBlockProps, 'className'>): ReactNode {
  if (isLoading) {
    return <Loader />
  }
  if (!hasParticipant) {
    return (
      <div className="text-sm text-muted-foreground">
        Link a participant to this note to list refill requests
      </div>
    )
  }
  if (meds.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No medications need a refill this visit
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      {meds.map((med) => {
        const medication = medicationById?.get(med.medicationRequestId)
        return (
          <div
            key={med.medicationRequestId}
            className="flex items-center gap-2 py-1 text-sm"
          >
            <TextLink
              to={`/Patient/${med.participantId}/medications/${med.medicationRequestId}`}
            >
              {med.display}
            </TextLink>
            {medication && (
              <MedicationElationDraftActions
                medication={medication}
                onDraftPrescription={onDraftPrescription}
                onOpenDraftPrescription={onOpenDraftPrescription}
                draftIdsByThreadId={draftIdsByThreadId}
                draftingMedicationIds={draftingMedicationIds}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
